import { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { UserProfile, Order, Notification, Chat } from '../types';

export const useProfile = (userId: string | undefined) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [chats, setChats] = useState<Chat[]>([]);
  const [wishlist, setWishlist] = useState<any[]>([]);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // 1. Fetch User Profile
  useEffect(() => {
    // 🟢 FIX 1: Explicitly clear data if it's a guest user
    if (!userId) {
      setProfile(null);
      setWishlist([]);
      setLoadingProfile(false);
      return;
    }

    const unsubProfile = onSnapshot(doc(db, "users", userId), (doc) => {
      if (doc.exists()) {
        setProfile({ id: doc.id, ...doc.data() } as UserProfile);
        // Also set local wishlist state if it exists on profile
        if (doc.data().wishlist) {
          setWishlist(doc.data().wishlist);
        }
      } else {
        setProfile(null);
      }
      setLoadingProfile(false);
    }, (error) => {
      console.error("Profile Fetch Error:", error);
      setLoadingProfile(false);
    });

    return () => unsubProfile();
  }, [userId]);

  // 2. Fetch User Orders
  useEffect(() => {
    // 🟢 FIX 1: Explicitly clear orders for guests
    if (!userId) {
      setOrders([]); 
      return;
    }

    const q = query(
      collection(db, "orders"),
      where("userId", "==", userId)
    );

    const unsubOrders = onSnapshot(q, (snapshot) => {
      const orderList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Order));
      
      // Sort manually in Javascript to avoid Firebase Index error
      orderList.sort((a: any, b: any) => b.createdAt - a.createdAt);
      
      setOrders(orderList);
    }, (error) => {
      // 🟢 FIX 2: Catch permission errors silently so they don't crash React
      console.error("Orders Fetch Error (Guest Mode):", error.message);
    });

    return () => unsubOrders();
  }, [userId]);

  // 3. Fetch Notifications
  useEffect(() => {
    // 🟢 FIX 1: Explicitly clear notifications for guests
    if (!userId) {
      setNotifications([]); 
      return;
    }

    const q = query(
      collection(db, "notifications"),
      where("userId", "==", userId)
    );

    const unsubNotifs = onSnapshot(q, (snapshot) => {
      setNotifications(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Notification)));
    }, (error) => {
       // 🟢 FIX 2: Catch permission errors silently
      console.error("Notifications Fetch Error (Guest Mode):", error.message);
    });

    return () => unsubNotifs();
  }, [userId]);

  // 4. Update Profile Helper
  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!userId) return;
    const { updateDoc, doc } = await import('firebase/firestore'); 
    await updateDoc(doc(db, "users", userId), data);
  };

  // 5. Toggle Wishlist Helper
  const toggleWishlist = async (product: any) => {
    if (!userId || !profile) return;
    const { updateDoc, arrayUnion, arrayRemove, doc } = await import('firebase/firestore');
    
    // Check if product is already in wishlist
    const productId = product.id;
    const exists = wishlist.some((item: any) => 
      (typeof item === 'string' && item === productId) || 
      (typeof item === 'object' && item.productId === productId)
    );

    const userRef = doc(db, "users", userId);

    if (exists) {
      // Remove
      const itemToRemove = wishlist.find(i => (i.productId === productId || i === productId));
      await updateDoc(userRef, {
        wishlist: arrayRemove(itemToRemove)
      });
    } else {
      // Add
      await updateDoc(userRef, {
        wishlist: arrayUnion({ productId: product.id, name: product.name, image: product.image || product.images?.[0] })
      });
    }
  };

  return { 
    profile, 
    loadingProfile, 
    orders, 
    notifications, 
    chats, 
    wishlist,
    updateProfile,
    setWishlist,
    toggleWishlist
  };
};